import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2, Send, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/contact`;

/** Contact form — replaces published email addresses. */
export default function ContactForm({ compact = false }) {
  const [form, setForm] = useState({
    name: '', email: '', organisation: '', subject: '', message: '', honeypot: '',
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(API, form);
      setSent(true);
      toast.success('Message sent — we reply within two working days');
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg
        || err?.response?.data?.detail || 'Could not send the message');
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/[0.06] p-5"
        data-testid="contact-form-sent">
        <p className="text-sm font-semibold flex items-center gap-2 mb-1">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Thank you — your message is with us.
        </p>
        <p className="text-[13px] text-muted-foreground">
          We reply within two working days. For account or billing questions, mention the email
          address you registered with.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="contact-form"
      className={`grid gap-3 ${compact ? '' : 'sm:grid-cols-2'}`}>
      <input type="text" value={form.honeypot} onChange={set('honeypot')} tabIndex={-1}
        autoComplete="off" className="hidden" aria-hidden="true" />
      <div>
        <Label htmlFor="cf-name" className="text-[12px]">Name</Label>
        <Input id="cf-name" required minLength={2} value={form.name} onChange={set('name')}
          data-testid="contact-name" />
      </div>
      <div>
        <Label htmlFor="cf-email" className="text-[12px]">Email</Label>
        <Input id="cf-email" type="email" required value={form.email} onChange={set('email')}
          data-testid="contact-email" />
      </div>
      <div>
        <Label htmlFor="cf-org" className="text-[12px]">Institution (optional)</Label>
        <Input id="cf-org" value={form.organisation} onChange={set('organisation')}
          data-testid="contact-org" />
      </div>
      <div>
        <Label htmlFor="cf-subject" className="text-[12px]">Subject</Label>
        <Input id="cf-subject" required minLength={2} value={form.subject} onChange={set('subject')}
          data-testid="contact-subject" />
      </div>
      <div className={compact ? '' : 'sm:col-span-2'}>
        <Label htmlFor="cf-msg" className="text-[12px]">Message</Label>
        <Textarea id="cf-msg" required minLength={10} rows={compact ? 4 : 5}
          value={form.message} onChange={set('message')} data-testid="contact-message"
          placeholder="How can we help? Please include your institution and, for billing questions, your registered email." />
      </div>
      <div className={compact ? '' : 'sm:col-span-2'}>
        <Button type="submit" disabled={busy} data-testid="contact-submit"
          className="bg-[#0b1a35] hover:bg-[#1d4ed8] text-white rounded-full">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
          Send message
        </Button>
        <p className="text-[11px] text-muted-foreground mt-2">
          We use your details only to answer this enquiry. No newsletter, no third-party sharing.
        </p>
      </div>
    </form>
  );
}
